import React, { useRef, useState, useEffect } from 'react';

import { Chip, Menu, MenuItem, MenuList } from '@mui/material';
import { grey } from '@mui/material/colors';
import { uniqueId } from 'lodash';

import type { TagOptionsType } from '../../../Data/types';

type Props = {
  items: TagOptionsType[];
};

const OverflowChips: React.FC<Props> = ({ items }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleChips, setVisibleChips] = useState<TagOptionsType[]>(items);
  const [overflowChips, setOverflowChips] = useState<TagOptionsType[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const resizeHandler = () => {
      if (!containerRef.current) return;
      const containerWidth = containerRef.current.offsetWidth;
      let availableWidth = containerWidth;
      const tempVisible: TagOptionsType[] = [];
      const tempOverflow: TagOptionsType[] = [];

      const testChip = document.createElement('div');
      testChip.style.visibility = 'hidden';
      testChip.style.position = 'absolute';
      document.body.appendChild(testChip);

      items.forEach((item) => {
        testChip.innerHTML = `<span class='MuiChip-label MuiChip-labelSmall'>${item.name}</span>`;
        testChip.className = 'MuiChip-root MuiChip-filled MuiChip-sizeSmall MuiChip-colorDefault MuiChip-filledDefault';
        testChip.style.backgroundColor = item.color ?? '#EFEFEF';
        const chipWidth = testChip.offsetWidth + 8; // 8px for margin
        // Check if the chip fits in the available width
        if (availableWidth - chipWidth > 30) {
          // Leave space for +more chip
          tempVisible.push(item);
          availableWidth -= chipWidth;
        } else {
          tempOverflow.push(item);
        }
      });

      testChip.remove();
      setVisibleChips(tempVisible);
      setOverflowChips(tempOverflow);
    };

    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, [items]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <div ref={containerRef} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {visibleChips.map((item) => (
        <Chip
          key={`${item.id}-${uniqueId()}`}
          label={item.name}
          size="small"
          sx={{
            backgroundColor: item.color ?? 'default',
            userSelect: 'none',
            color: item.color ? grey[900] : 'inherit',
            cursor: 'pointer',
          }}
        />
      ))}
      {overflowChips.length > 0 && (
        <Chip label={`+${overflowChips.length} more`} key="overflow-chip" onClick={handleMenuOpen} size="small" />
      )}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuList dense>
          {overflowChips.map((item) => (
            <MenuItem key={`${item.id}-menu-${uniqueId()}`} onClick={handleMenuClose}>
              {item.name}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </div>
  );
};

export default OverflowChips;
